import { useEffect, useCallback, useRef } from 'react';
import confetti from 'canvas-confetti';
import { toast } from 'sonner';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

/**
 * MilestoneCelebrations — invisible. Polls /wellness/celebrations on mount and
 * after any `wellness:refresh`, then fires a confetti burst + a clean toast for
 * each NEW milestone (level-up, streak, complete day). Copy is symbol-free to
 * stay on-brand; the backend guarantees each milestone fires exactly once.
 */
const fire = () => {
  const colors = ['#CCFF00', '#ffffff', '#a3e635'];
  const opts = { disableForReducedMotion: true };
  confetti({ ...opts, particleCount: 90, spread: 70, origin: { y: 0.3 }, colors });
  setTimeout(() => confetti({ ...opts, particleCount: 50, angle: 60, spread: 55, origin: { x: 0, y: 0.4 }, colors }), 120);
  setTimeout(() => confetti({ ...opts, particleCount: 50, angle: 120, spread: 55, origin: { x: 1, y: 0.4 }, colors }), 240);
};

export const MilestoneCelebrations = () => {
  const { user } = useAuth();
  const busy = useRef(false);

  const check = useCallback(async () => {
    if (busy.current || !user) return;
    busy.current = true;
    try {
      const { data } = await api.getCelebrations();
      const milestones = data?.milestones || [];
      milestones.forEach((m, i) => {
        setTimeout(() => {
          fire();
          toast.success(m.title, { description: m.detail, duration: 5000 });
        }, i * 900);
      });
    } catch {
      /* non-blocking */
    } finally {
      busy.current = false;
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    check();
    const onRefresh = () => check();
    window.addEventListener('wellness:refresh', onRefresh);
    return () => window.removeEventListener('wellness:refresh', onRefresh);
  }, [user, check]);

  return null;
};

export default MilestoneCelebrations;
